import React from "react";

/* eslint-disable unused-imports/no-unused-vars */

/**
 * Represents a type of block that can appear in a StreamField.
 */
type StreamFieldBlock<Type extends string, Value> = {
    id: string;
    type: Type;
    value: Value;
};

/**
 * Represents an array of block values (the value of a StreamField on a page).
 */
type StreamFieldValue<Type extends string, Value> = StreamFieldBlock<
    Type,
    Value
>[];

/**
 * A mapped type of each possible StreamField block type to it's expected value
 * type.
 */
type StreamFieldBlockValueTypeMap<FieldValue> =
    FieldValue extends StreamFieldValue<infer Type, infer Value>
        ? {
              [Block in FieldValue[number] as Block["type"]]: Block["value"];
          }
        : never;

/**
 * A mapped type of each possible StreamField block type to the React component
 * which is used to render the block.
 */
export type StreamFieldBlockMap<FieldValue> =
    FieldValue extends StreamFieldValue<infer Type, infer Value>
        ? {
              [Block in FieldValue[number] as Block["type"]]: React.ComponentType<{
                  id?: Block["id"];
                  type?: Block["type"];
                  index?: number;
                  value: Block["value"];
              }>;
          }
        : never;

/**
 * StreamField React component props
 */
export type StreamFieldProps<FieldValue> =
    FieldValue extends StreamFieldValue<infer Type, infer Value>
        ? {
              components: StreamFieldBlockMap<FieldValue>;
              value: FieldValue;
              tagName?: keyof React.ReactHTML | typeof React.Fragment;
              attrs?: React.HTMLAttributes<HTMLElement>;
          }
        : never;

/**
 * StreamField React component
 */
export const StreamField = <FieldValue = [],>(
    props: StreamFieldProps<FieldValue>,
) => {
    const tagName: keyof React.ReactHTML | typeof React.Fragment =
        props.tagName ?? "div";
    const children = props.value.map((block, i) => {
        const BlockComponent = props.components[block.type];
        return (
            <BlockComponent
                key={block.id}
                id={block.id}
                type={block.type}
                index={i}
                value={block.value}
            />
        );
    });
    return React.createElement(tagName, props.attrs, children);
};
